// Mock data for Innercity Creative Group LLC

export const companyInfo = {
  name: "Innercity Creative Group",
  suffix: "LLC",
  founder: "Monica Lockett",
  founderRole: "CEO & Lead Designer",
  foundedYear: 2004,
  tagline: "A Creative & Digital Agency",
  headline: "We help brands stand out from the crowd",
  subheadline:
    "Innovative ideas and top-notch services for small businesses ready to make a big statement.",
  phone: "+1 (214) 840-3425",
  email: "info@innercitycreativegroup.com",
  workingHours: "Mon – Fri · 9:00 – 18:00",
  address: "Dallas, TX, USA",
};

export const founder = {
  name: "Monica Lockett",
  role: "CEO & Lead Designer",
  discipline: "Senior Product Designer · Design Systems · AI-Enabled UX",
  linkedin: "https://www.linkedin.com/in/monicalockett/",
  portfolio: "https://monicalockett.com",
  image:
    "https://customer-assets-jai6qajn.emergentagent.net/job_creative-collective-23/artifacts/riultkvq_1585242122041.jpeg",
  bio: "Monica founded Innercity Creative Group in 2004 with a simple belief: great design shouldn't be a privilege reserved for enterprise budgets. Today she leads the studio from Dallas as CEO and Lead Designer with 15+ years of product design experience — enterprise applications, SaaS platforms, responsive web, operational systems, and mobile — across telecom, insurance, utilities, finance, and enterprise technology.",
  focus:
    "Human-centered digital experiences shaped by thoughtful research, design systems thinking, AI-assisted UX workflows, and accessibility that meets WCAG 2.0 AA.",
  clients: [
    "Comcast",
    "AT&T",
    "IBM",
    "Experian",
    "CopperPoint",
    "State Farm",
    "DTE Energy",
    "The Hartford",
    "Accenture",
  ],
  toolbelt: [
    "Figma & Figma AI",
    "Adobe Creative Cloud",
    "FigJam",
    "Builder.io",
    "Material UI",
    "Tailwind CSS",
    "React collaboration",
    "Jira / Rally / Aha!",
  ],
};

export const experience = [
  {
    period: "2019 — Present",
    role: "Senior Product Designer / UX Design Systems Consultant",
    org: "Innercity Creative Group LLC",
    location: "Dallas, TX",
    note:
      "Leading scalable design systems, component libraries, and AI-assisted UX workflows for enterprise clients.",
  },
  {
    period: "2024 — 2025",
    role: "Senior UX Designer",
    org: "CopperPoint Insurance",
    location: "Mesquite, TX / Remote",
    note:
      "Enterprise UX workflows, operational systems, and responsive UI in Agile / Scrum.",
  },
  {
    period: "2023 — 2024",
    role: "User Experience Designer",
    org: "Experian (via TCS)",
    location: "Remote",
    note:
      "Redesigned the Global Security Operations Center intranet; enterprise UX modernization and accessibility.",
  },
  {
    period: "2022 — 2023",
    role: "Senior UX Designer",
    org: "Comcast",
    location: "Remote",
    note:
      "Led the redesign of the FIS Comcast Payment Center on Angular + Material Design.",
  },
  {
    period: "2021 — 2022",
    role: "Senior UX Designer",
    org: "AT&T (via Tech Mahindra)",
    location: "Remote",
    note:
      "Research-led design of AT&T's in-store buyer process for employee-facing iPad workflows.",
  },
  {
    period: "2018 — 2019",
    role: "UI/UX Designer & Developer",
    org: "IBM",
    location: "Plano, TX",
    note:
      "Redesigned Smart Meter Texas serving 74M+ users; widget-based portal for faster iteration.",
  },
];

export const navLinks = [
  { label: "Work", href: "/#work" },
  { label: "Services", href: "/#services" },
  { label: "About", href: "/#about" },
  { label: "Testimonials", href: "/#testimonials" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Contact", href: "/#contact" },
];

export const services = [
  {
    id: 1,
    title: "Creative Design",
    subtitle: "Brand & Visual Identity",
    description:
      "A helpful pit stop for refuelling any creative design need. Stay unique — say no to templates.",
    features: ["Logo & identity", "Print & packaging", "Social creatives"],
  },
  {
    id: 2,
    title: "UX & Product Design",
    subtitle: "Research-driven experiences",
    description:
      "Human-centered design led by Monica's 20+ years across enterprise UX — from dashboards and design systems to consumer apps.",
    features: ["UX research", "Design systems", "Product design"],
  },
  {
    id: 3,
    title: "Web Development",
    subtitle: "Coding & Usability",
    description:
      "We surface issues before a single line of code is written — the earlier they're found, the cheaper they are to fix.",
    features: ["Custom websites", "E-commerce", "Web applications"],
  },
  {
    id: 4,
    title: "Brand Consulting",
    subtitle: "Strategy & Positioning",
    description:
      "Smart brand strategy to build, manage and grow real brand value in a crowded market.",
    features: ["Brand strategy", "Naming", "Voice & messaging"],
  },
  {
    id: 5,
    title: "Digital Marketing",
    subtitle: "Growth & Performance",
    description:
      "Target-oriented marketing strategies that drive real traffic and real online sales — not vanity metrics.",
    features: ["SEO", "Paid ads", "Content strategy"],
  },
  {
    id: 6,
    title: "Video & Motion",
    subtitle: "Editing & Post",
    description:
      "From social cutdowns to event highlights and brand films — video that gets watched all the way through.",
    features: ["Editing", "Motion graphics", "Event coverage"],
  },
];

export const skills = [
  { label: "UX & Product Design", value: 96 },
  { label: "Design Systems", value: 92 },
  { label: "Web Development", value: 88 },
  { label: "Brand & Identity", value: 90 },
];

export const whyChooseUs = [
  {
    id: 1,
    title: "Exceptional Customer Service",
    body: "Outstanding service is our USP. We don't leave any stone unturned — from first call to long after launch.",
  },
  {
    id: 2,
    title: "Enterprise-grade UX",
    body: "Monica has led UX for Comcast, AT&T, UPS, State Farm and IBM — that thinking now powers every ICG project.",
  },
  {
    id: 3,
    title: "High-Performance Web",
    body: "Visually sharp, user-friendly and technically solid — solutions that deliver tangible business results.",
  },
  {
    id: 4,
    title: "Two Decades of Experience",
    body: "Since 2004 we've been trusted by brands to deliver excellence — quietly, consistently, and on time.",
  },
  {
    id: 5,
    title: "Research-Driven Process",
    body: "Every project starts with real users and real business goals — not assumptions or template thinking.",
  },
  {
    id: 6,
    title: "Competitive Pricing",
    body: "Cutting-edge craft and marketing at prices small businesses can actually say yes to.",
  },
];

export const testimonials = [
  {
    id: 1,
    quote:
      "Monica's Doc Center redesign transformed how our teams work. Her research-driven approach and attention to real user needs cut search time significantly. She genuinely understands enterprise workflows.",
    name: "Sarah Chen",
    role: "VP of Product",
    company: "Comcast",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=face",
  },
  {
    id: 2,
    quote:
      "Working with Monica on Partner Exchange was a game-changer. She unified five disconnected systems into one experience our partners actually love. Her ability to map complex flows and make them feel simple is remarkable.",
    name: "Michael Thompson",
    role: "Director of Digital Experience",
    company: "AT&T",
    image:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face",
  },
  {
    id: 3,
    quote:
      "The design system Monica built for us reduced agent training time by roughly 30%. Her systematic thinking and clear documentation made adoption seamless across every tool.",
    name: "Emily Rodriguez",
    role: "Product Manager",
    company: "State Farm",
    image:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face",
  },
  {
    id: 4,
    quote:
      "Monica brought our community vision to life. The mobile app she designed launched with a 4.5-star rating, and most new users made a real local connection in their first month. Her user research was invaluable.",
    name: "David Park",
    role: "Founder & CEO",
    company: "Circulus",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face",
  },
  {
    id: 5,
    quote:
      "Monica understood our contractors immediately. She spent time on-site learning their workflow and designed an e-commerce experience that took our digital orders from 5% to 45%. Practical, user-centered, exactly what we needed.",
    name: "Jennifer Walsh",
    role: "Head of UX",
    company: "Builders First Source",
    image:
      "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=200&h=200&fit=crop&crop=face",
  },
];

export const pricing = [
  {
    id: "01",
    name: "Basic Website",
    tagline: "For getting online, fast",
    price: "Starts at $1,200",
    features: [
      "Up to 10 pages",
      "Hand-coded HTML / CSS / JS",
      "Mobile responsive",
      "Basic on-page SEO",
    ],
  },
  {
    id: "02",
    name: "Advanced Website",
    tagline: "Own your content",
    price: "Starts at $3,500",
    highlighted: true,
    features: [
      "CMS you can edit yourself",
      "Blog & landing pages",
      "Integrations & forms",
      "Analytics & SEO setup",
    ],
  },
  {
    id: "03",
    name: "Custom Website",
    tagline: "A larger site, built to fit",
    price: "Custom quote",
    features: [
      "Custom design & UX",
      "Web applications",
      "E-commerce & memberships",
      "Ongoing partnership",
    ],
  },
];

export const branches = [
  { city: "DFW", role: "General Office", address: "Dallas, TX, USA" },
  { city: "Detroit", role: "Office", address: "Michigan, USA" },
  {
    city: "Chandigarh",
    role: "Office",
    address: "Sector 66, Sahibzada Ajit Singh Nagar, India",
  },
];

export const stats = [
  { value: "20+", label: "Years in business" },
  { value: "15+", label: "Years of UX practice" },
  { value: "9", label: "Fortune 500 clients" },
  { value: "3", label: "Offices worldwide" },
];

export const clientLogos = [
  "Comcast",
  "AT&T",
  "IBM",
  "Experian",
  "CopperPoint",
  "State Farm",
  "DTE Energy",
  "The Hartford",
  "Accenture",
];
